import React, { useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppDialogFrame } from '../../components/AppDialogFrame';
import { AppInput } from '../../components/AppInput';
import { ProjectMemberPicker } from '../../components/ProjectMemberPicker';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { createProjectTask } from '../../services/projectService';
import type { ProjectBoardColumn, ProjectMember } from '../../types/project';
import { todayBusinessCalendarDate } from '../../utils/dateTime';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  nextTaskDraftKey,
  validateCreateTaskForm,
  type TaskFormFieldErrors,
  type TaskSubtaskDraft,
} from '../../utils/projectTaskForm';
import { runOperation } from '../../utils/operationFeedback';
import type { CompletedOnChoice } from '../../utils/taskCompletionDate';

type Props = {
  visible: boolean;
  projectId: number;
  column: ProjectBoardColumn | null;
  members: ProjectMember[];
  onClose: () => void;
  onCreated: () => void;
};

export function CreateTaskModal({
  visible,
  projectId,
  column,
  members,
  onClose,
  onCreated,
}: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      title: { ...typography.sectionTitle, color: palette.textPrimary, flex: 1 },
      close: { ...typography.body, color: palette.textSecondary, fontWeight: '700' as const },
      section: {
        ...typography.caption,
        color: palette.textPrimary,
        fontWeight: '700' as const,
        flex: 1,
      },
      label: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      icon: { ...typography.body, color: palette.primary, fontWeight: '700' as const },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const [taskName, setTaskName] = useState('');
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [completedOn, setCompletedOn] = useState<CompletedOnChoice>('today');
  const [customCompletedDate, setCustomCompletedDate] = useState(
    () => todayBusinessCalendarDate(),
  );
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [subtasks, setSubtasks] = useState<TaskSubtaskDraft[]>([]);
  const [fieldErrors, setFieldErrors] = useState<TaskFormFieldErrors>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function reset() {
    setTaskName('');
    setAssignedEmployeeIds([]);
    setDueDate('');
    setTimeEstimate('');
    setCompletedOn('today');
    setCustomCompletedDate(todayBusinessCalendarDate());
    setDescriptionOpen(false);
    setDescription('');
    setSubtasks([]);
    setFieldErrors({});
    setError('');
    setBusy(false);
  }

  function close() {
    if (busy) return;
    reset();
    onClose();
  }

  const requiresDetails = column?.requiresTaskDetails === true;
  const requireCompletedDate = column?.isCompletedColumn === true;

  function addSubtask(afterKey?: string) {
    const next: TaskSubtaskDraft = {
      key: nextTaskDraftKey('subtask'),
      name: '',
      assignedEmployeeIds: [],
    };
    setSubtasks(current => {
      if (!afterKey) return [...current, next];
      const index = current.findIndex(item => item.key === afterKey);
      if (index < 0) return [...current, next];
      const copy = [...current];
      copy.splice(index + 1, 0, next);
      return copy;
    });
  }

  async function save() {
    if (busy || !column?.projectColumnId) return;
    const result = validateCreateTaskForm({
      taskName,
      assignedEmployeeIds,
      dueDate,
      timeEstimate,
      description,
      descriptionOpen,
      subtasks,
      requiresTaskDetails: requiresDetails,
      requireCompletedDate,
      completedOn,
      customCompletedDate,
      columnName: column.columnName,
    });
    setFieldErrors(result.fieldErrors);
    if (!result.payload) {
      return;
    }
    const payload = result.payload;
    const projectColumnId = column.projectColumnId;
    setBusy(true);
    setError('');
    try {
      await runOperation('add', () => createProjectTask(projectId, {
        ...payload,
        projectColumnId,
      }));
      reset();
      onCreated();
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to save the task.'));
    } finally {
      setBusy(false);
    }
  }

  if (!visible || !column) return null;

  return (
    <AppDialogFrame visible onRequestClose={close}>
      <View style={[styles.flex, { backgroundColor: palette.background }]}>
        <View style={styles.header}>
          <Text style={themed.title}>Create Task</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={close}
            hitSlop={8}
          >
            <Text style={themed.close}>X</Text>
          </Pressable>
        </View>
        <ScrollView
          contentContainerStyle={styles.body}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={[styles.columnHint, { color: palette.textSecondary }]}>
            Column: {column.columnName}
          </Text>

          <AppInput
            label="Task Name"
            value={taskName}
            onChangeText={setTaskName}
            placeholder="Enter task name"
            error={fieldErrors.taskName}
          />

          {requireCompletedDate ? (
            <>
              <Text style={themed.label}>Completed on *</Text>
              <View style={styles.choiceRow}>
                {(
                  [
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'today', label: 'Today' },
                    { value: 'custom', label: 'Other date' },
                  ] as const
                ).map(choice => {
                  const selected = completedOn === choice.value;
                  return (
                    <Pressable
                      key={choice.value}
                      accessibilityRole="button"
                      accessibilityLabel={choice.label}
                      accessibilityState={{ selected }}
                      onPress={() => {
                        setCompletedOn(choice.value);
                        if (choice.value === 'custom') {
                          setCustomCompletedDate('');
                        }
                        setFieldErrors(current => ({
                          ...current,
                          completedDate: undefined,
                        }));
                      }}
                      style={[
                        styles.choice,
                        {
                          borderColor: selected ? palette.primary : palette.border,
                          backgroundColor: selected
                            ? palette.primary
                            : palette.surface,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.choiceText,
                          {
                            color: selected
                              ? palette.onPrimary
                              : palette.textPrimary,
                          },
                        ]}
                      >
                        {choice.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
              {completedOn === 'custom' ? (
                <AppDateField
                  label="Completion date *"
                  value={customCompletedDate}
                  onChange={value => {
                    setCustomCompletedDate(value);
                    setFieldErrors(current => ({
                      ...current,
                      completedDate: undefined,
                    }));
                  }}
                  placeholder="Select date"
                  error={fieldErrors.completedDate}
                  maximumDate={new Date()}
                />
              ) : fieldErrors.completedDate ? (
                <Text accessibilityRole="alert" style={themed.error}>
                  {fieldErrors.completedDate}
                </Text>
              ) : null}
            </>
          ) : null}

          <View style={styles.infoRow}>
            <View style={styles.infoField}>
              <ProjectMemberPicker
                label={requiresDetails ? 'Members *' : 'Members'}
                members={members}
                selectedIds={assignedEmployeeIds}
                onChange={setAssignedEmployeeIds}
                error={fieldErrors.member}
              />
            </View>
            <View style={styles.infoField}>
              <AppDateField
                label={requiresDetails ? 'Due Date *' : 'Due Date'}
                value={dueDate}
                onChange={setDueDate}
                placeholder="Select date"
                error={fieldErrors.dueDate}
              />
            </View>
            <View style={styles.infoField}>
              <AppInput
                label={requiresDetails ? 'Time Estimate *' : 'Time Estimate'}
                value={timeEstimate}
                onChangeText={setTimeEstimate}
                placeholder="00:00"
                error={fieldErrors.timeEstimate}
                autoCapitalize="none"
                keyboardType="numbers-and-punctuation"
              />
            </View>
          </View>

          <View style={styles.sectionHeader}>
            <Text style={themed.section}>Description</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={
                descriptionOpen ? 'Hide description' : 'Add description'
              }
              onPress={() => {
                if (descriptionOpen) {
                  setDescription('');
                  setDescriptionOpen(false);
                  return;
                }
                setDescriptionOpen(true);
              }}
              hitSlop={8}
            >
              <Text style={themed.icon}>{descriptionOpen ? '−' : '+'}</Text>
            </Pressable>
          </View>
          {descriptionOpen ? (
            <TextInput
              accessibilityLabel="Description"
              value={description}
              onChangeText={setDescription}
              placeholder="Enter task description here..."
              placeholderTextColor={palette.textMuted}
              multiline
              textAlignVertical="top"
              style={[
                styles.description,
                {
                  borderColor: palette.border,
                  backgroundColor: palette.surface,
                  color: palette.textPrimary,
                },
              ]}
            />
          ) : null}

          <View style={styles.sectionHeader}>
            <Text style={themed.section}>Subtasks</Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Add Subtask"
              onPress={() => addSubtask()}
              hitSlop={8}
            >
              <Text style={themed.icon}>+</Text>
            </Pressable>
          </View>
          {subtasks.map(item => (
            <View key={item.key} style={styles.subtaskBlock}>
              <View style={styles.subtaskRow}>
                <TextInput
                  accessibilityLabel="Subtask name"
                  value={item.name}
                  onChangeText={value =>
                    setSubtasks(current =>
                      current.map(row =>
                        row.key === item.key ? { ...row, name: value } : row,
                      ),
                    )
                  }
                  placeholder="Subtask name"
                  placeholderTextColor={palette.textMuted}
                  style={[
                    styles.subtaskInput,
                    {
                      borderColor: palette.border,
                      backgroundColor: palette.surface,
                      color: palette.textPrimary,
                    },
                  ]}
                />
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Add Subtask"
                  onPress={() => addSubtask(item.key)}
                  hitSlop={8}
                >
                  <Text style={themed.icon}>+</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Remove subtask"
                  onPress={() =>
                    setSubtasks(current => current.filter(row => row.key !== item.key))
                  }
                  hitSlop={8}
                >
                  <Text style={[themed.icon, { color: palette.error }]}>−</Text>
                </Pressable>
              </View>
              <ProjectMemberPicker
                label="Subtask members"
                members={members}
                selectedIds={item.assignedEmployeeIds}
                onChange={ids =>
                  setSubtasks(current =>
                    current.map(row =>
                      row.key === item.key
                        ? { ...row, assignedEmployeeIds: ids }
                        : row,
                    ),
                  )
                }
                accessibilityLabel={`Assign members to ${item.name || 'subtask'}`}
              />
            </View>
          ))}
          {fieldErrors.subtasks ? (
            <Text style={themed.error}>{fieldErrors.subtasks}</Text>
          ) : null}

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}

          <View style={styles.actions}>
            <AppButton title="Cancel" variant="secondary" onPress={close} />
            <AppButton title="Save" onPress={save} loading={busy} />
          </View>
        </ScrollView>
      </View>
    </AppDialogFrame>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  body: {
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xxl,
    gap: spacing.md,
  },
  columnHint: { ...typography.caption },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  choice: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  choiceText: {
    ...typography.caption,
    fontWeight: '700',
  },
  infoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  infoField: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  description: {
    ...typography.input,
    minHeight: 96,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  subtaskBlock: { gap: spacing.sm },
  subtaskRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  subtaskInput: {
    ...typography.input,
    flex: 1,
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
});
